
import { getGravatarURL } from '@jitsi/js-utils/avatar';
import i18next from 'i18next';
import type { Store } from 'redux';

import logger from '../../app/logger';
import type { IParticipant, IStateful } from '../../app/types';
import { getFirstName } from '../../display-name/functions';
import { showWarningNotification } from '../../notifications/actions';
import { extractHsl } from '../environment/environment';
import { ADD_PEOPLE_ENABLED } from '../flags/constants';
import { getFeatureFlag } from '../flags/functions';
import { MHParticipantConnectionStatus } from '../lib-meet-hour';
import { VIDEO_TYPE } from '../media/constants';
import { shouldRenderVideoTrack } from '../media/functions';
import { FakeParticipant } from '../mtApi/constants';
import { toState } from '../redux';
import { getScreenShareTrack } from '../tracks/functions';
import { createDeferred } from '../util';

import { SET_PARTICIPANT_COLOR } from './actionTypes';
import {
    JIGASI_PARTICIPANT_ICON,
    MAX_DISPLAY_NAME_LENGTH,
    PARTICIPANT_ROLE
} from './constants';
import { preloadImage } from './preloadImage';

declare var APP: Object;
declare var interfaceConfig: Object;

/**
 * Sends the local participant's own tile color, to everyone or to one participant.
 *
 * Each client announces only its own color and takes a participant's color only from that participant (see
 * {@link isOwnColorReport}). Previously every client re-broadcast every participant's color, plus a private copy
 * to the newcomer, on every join: ~2N² room messages per join, which in large rooms hit Prosody's MUC rate
 * limit (conference.chatError on all clients) and flooded the XMPP connection and the redux store.
 *
 * @param {Object|Function} stateful - The redux state or getState.
 * @param {string} [to] - The participant (or hidden participant, e.g. a recorder) to send to; all if empty.
 * @returns {void}
 */
export function sendLocalParticipantColor(stateful: Object | Function, to: string = '') {
    const state = toState(stateful);
    const { conference } = state['features/base/conference'];
    const color = getLocalParticipant(state)?.color;

    if (!conference?.isJoined() || !color || state['features/base/config']?.iAmRecorder) {
        return;
    }

    const myId = conference.myUserId();

    conference.sendMessage({
        type: SET_PARTICIPANT_COLOR,
        participantId: myId,
        color: extractHsl(color),
        senderId: myId
    }, to);
}

/**
 * Whether a received SET_PARTICIPANT_COLOR message reports the sender's own color. Reports about other
 * participants (still sent by older clients) are ignored so a participant's color has a single owner.
 *
 * @param {Object|string} sender - The sender: an MHParticipant (ENDPOINT_MESSAGE_RECEIVED) or the id of a
 * hidden participant (NON_PARTICIPANT_MESSAGE_RECEIVED).
 * @param {Object} data - The message.
 * @returns {boolean}
 */
export function isOwnColorReport(sender: Object | string, data: Object) {
    const senderId = typeof sender === 'string' ? sender : sender?.getId?.();

    return Boolean(senderId) && data?.participantId === senderId;
}

/**
 * Temp structures for avatar urls to be checked/preloaded.
 */
const AVATAR_QUEUE = [];
const AVATAR_CHECKED_URLS = new Map();
const AVATAR_CHECKER_FUNCTIONS = [
    participant => {
        if (participant && participant.isJigasi) {
            return JIGASI_PARTICIPANT_ICON;
        }

        return null;
    },
    participant => {
        if (participant && participant.avatarURL) {
            return participant.avatarURL;
        }

        return null;
    },
    (participant, store) => {

        const regex = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;

        if (participant && participant.email && participant.email.match(regex)) {
            // TODO: remove once libravatar has deployed their new scaled up infra. -saghul
            const gravatarBaseURL
                = store.getState()['features/base/config'].gravatarBaseURL ?? 'https://www.gravatar.com/avatar/';
            const gravatarURL = getGravatarURL(participant.email, gravatarBaseURL);
            const gravatarURLis404 = gravatarURL.includes('d=404');

            return gravatarURLis404 ? null : gravatarURL;
        }

        return null;
    }
];
/* eslint-enable arrow-body-style, no-unused-vars */

/**
 * Returns the list of active speakers that should be moved to the top of the sorted list of participants so that the
 * dominant speaker is visible always on the vertical filmstrip in stage layout.
 *
 * @param {Function | Object} stateful - The (whole) redux state, or redux's {@code getState} function to be used to
 * retrieve the state.
 * @returns {Array<string>}
 */
export function getActiveSpeakersToBeDisplayed(stateful) {
    const state = toState(stateful);
    const {
        dominantSpeaker,
        fakeParticipants,
        sortedRemoteVirtualScreenshareParticipants,
        speakersList
    } = state['features/base/participants'];
    const { visibleRemoteParticipants } = state['features/filmstrip'];
    let activeSpeakers = new Map(speakersList);

    // Do not re-sort the active speakers if dominant speaker is currently visible.
    if (dominantSpeaker && visibleRemoteParticipants.has(dominantSpeaker)) {
        return activeSpeakers;
    }
    let availableSlotsForActiveSpeakers = visibleRemoteParticipants.size;

    if (activeSpeakers.has(dominantSpeaker ?? '')) {
        activeSpeakers.delete(dominantSpeaker ?? '');
    }

    // Add dominant speaker to the beginning of the list (not including self) since the active speaker list is always
    // alphabetically sorted.
    if (dominantSpeaker && dominantSpeaker !== getLocalParticipant(state)?.id) {
        const updatedSpeakers = Array.from(activeSpeakers);

        updatedSpeakers.splice(0, 0, [ dominantSpeaker, getParticipantById(state, dominantSpeaker)?.name ?? '' ]);
        activeSpeakers = new Map(updatedSpeakers);
    }

    // Remove screenshares from the count.
    if (sortedRemoteVirtualScreenshareParticipants) {
        availableSlotsForActiveSpeakers -= sortedRemoteVirtualScreenshareParticipants.size * 2;
        for (const screenshare of Array.from(sortedRemoteVirtualScreenshareParticipants.keys())) {
            const ownerId = getVirtualScreenshareParticipantOwnerId(screenshare);

            activeSpeakers.delete(ownerId);
        }
    }

    // Remove fake participants from the count.
    if (fakeParticipants) {
        availableSlotsForActiveSpeakers -= fakeParticipants.size;
    }
    const truncatedSpeakersList = Array.from(activeSpeakers).slice(0, availableSlotsForActiveSpeakers);

    truncatedSpeakersList.sort((a, b) => a[1].localeCompare(b[1]));

    return new Map(truncatedSpeakersList);
}

/**
 * Returns participant ID of the owner of a virtual screenshare participant.
 *
 * Callers routinely pass an ID that is legitimately absent - `features/large-video` has no
 * `participantId` until SELECT_LARGE_VIDEO_PARTICIPANT first fires, and a thumbnail can render before its
 * participant is in the store - and dereferencing it produced an uncaught
 * "TypeError: undefined is not an object (evaluating 'id.endsWith')" from inside a `mapStateToProps`,
 * which takes down the React tree rather than degrading. The declared return type already admits
 * `undefined` and every caller passes the result to `getParticipantById` or compares it, so answering
 * "no owner" for "no participant" is the contract they were already written against.
 *
 * @param {string} [id] - The ID of the virtual screenshare participant.
 * @private
 * @returns {(string|undefined)}
 */
export function getVirtualScreenshareParticipantOwnerId(id) {
    if (typeof id !== 'string') {
        return undefined;
    }

    if (id.endsWith('-screenshare')) {
        return id.replace('-screenshare', '');
    }

    // Source names follow the format: endpointId-v0, endpointId-a1, etc.
    // Strip the trailing -{v|a}{digit(s)} suffix to get the full endpoint ID,
    // which may itself contain hyphens (e.g. UUID-style IDs).
    return id.replace(/-[va]\d+$/, '');
}

/**
 * Resolves the first loadable avatar URL for a participant.
 *
 * @param {Object} participant - The participant to resolve avatars for.
 * @param {Store} store - Redux store.
 * @returns {Promise}
 */
export function getFirstLoadableAvatarUrl(participant: Object, store: Store<any, any>) {
    const deferred = createDeferred();
    const fullPromise = deferred.promise
        .then(() => _getFirstLoadableAvatarUrl(participant, store))
        .then(src => {

            if (AVATAR_QUEUE.length) {
                const next = AVATAR_QUEUE.shift();

                next.resolve();
            }

            return src;
        });

    if (AVATAR_QUEUE.length) {
        AVATAR_QUEUE.push(deferred);
    } else {
        deferred.resolve();
    }

    return fullPromise;
}

/**
 * Returns the participant with the ID matching the passed ID or the local participant if the ID is
 * undefined.
 *
 * @param {(Function|Object)} stateful - The (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state
 * features/base/participants.
 * @param {string|undefined} [participantID] - An optional partipantID argument.
 * @returns {Participant|undefined}
 */
export function getParticipantByIdOrUndefined(stateful: Object | Function, participantID: ?string) {
    return participantID ? getParticipantById(stateful, participantID) : getLocalParticipant(stateful);
}

/**
 * Returns local participant from Redux state.
 *
 * @param {(Function|Object|Participant[])} stateful - The redux state
 * features/base/participants, the (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state
 * features/base/participants.
 * @returns {(Participant|undefined)}
 */
export function getLocalParticipant(stateful: Object | Function) {
    const participants = _getAllParticipants(stateful);

    return participants.find(p => p?.local);
}

/**
 * Normalizes a display name so then no invalid values (padding, length...etc)
 * can be set.
 *
 * @param {string} name - The display name to set.
 * @returns {string}
 */
export function getNormalizedDisplayName(name: string) {
    if (!name || !name.trim()) {
        return undefined;
    }

    return name.trim().substring(0, MAX_DISPLAY_NAME_LENGTH);
}

/**
 * Returns participant by ID from Redux state.
 *
 * @param {(Function|Object|Participant[])} stateful - The redux state
 * features/base/participants, the (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state
 * features/base/participants.
 * @param {string} id - The ID of the participant to retrieve.
 * @private
 * @returns {(Participant|undefined)}
 */
export function getParticipantById(
        stateful: Object | Function, id: string): ?Object {
    const participants = _getAllParticipants(stateful);

    return participants.find(p => p.id === id);
}

/**
 * Returns a count of the known participants in the passed in redux state,
 * excluding any fake participants.
 *
 * @param {(Function|Object|Participant[])} stateful - The redux state
 * features/base/participants, the (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state
 * features/base/participants.
 * @returns {number}
 */
export function getParticipantCount(stateful: Object | Function) {
    return getParticipants(stateful).length;
}

/**
 * Returns a count of the known participants in the passed in redux state,
 * including fake participants.
 *
 * @param {(Function|Object|Participant[])} stateful - The redux state
 * features/base/participants, the (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state
 * features/base/participants.
 * @returns {number}
 */
export function getParticipantCountWithFake(stateful: Object | Function) {
    return _getAllParticipants(stateful).length;
}

/**
 * Returns participant's display name.
 *
 * FIXME: Remove the hardcoded strings once interfaceConfig is stored in redux
 * and merge with a similarly named method in {@code conference.js}.
 *
 * @param {(Function|Object)} stateful - The (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state.
 * @param {string} id - The ID of the participant's display name to retrieve.
 * @returns {string}
 */
export function getParticipantDisplayName(
        stateful: Object | Function,
        id: string) {
    const participant = getParticipantById(stateful, id);
    const state = toState(stateful);
    const sharingParticipant = getParticipantById(stateful, state['features/shared-video']?.ownerId);
    const isLocalYoutubeShare = sharingParticipant?.id === id;

    if (participant) {
        if (isLocalScreenshareParticipant(participant)) {
            return `You ${i18next.t('toolbar.screenshare')}`;
        }

        if (isLocalYoutubeShare && participant.name === 'YouTube') {
            return i18next.t('dialog.youtubeSharingUser', { displayName: 'You' });
        }

        if (!isLocalYoutubeShare && participant.name === 'YouTube') {
            return i18next.t('dialog.youtubeSharingUser', { displayName: getFirstName(sharingParticipant?.name) });
        }

        if (isScreenShareParticipant(participant)) {
            return `${participant.name} ${i18next.t('toolbar.screenshare')}`;
        }

        if (participant.name) {
            return participant.name;
        }

        if (participant.local) {
            return typeof interfaceConfig === 'object'
                ? interfaceConfig.DEFAULT_LOCAL_DISPLAY_NAME
                : 'me';
        }
    }

    return typeof interfaceConfig === 'object'
        ? interfaceConfig.DEFAULT_REMOTE_DISPLAY_NAME
        : 'Guest';
}

/**
 * Returns participant's designation.
 *
 * @param {(Function|Object)} stateful - The (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state.
 * @param {string} id - The ID of the participant's designation to retrieve.
 * @returns {string}
 */
export function getParticipantDesignation(
        stateful: Object | Function,
        id: string) {
    const participant = getParticipantById(stateful, id);

    return participant?.designation;
}

/**
 * Returns participant's organization.
 *
 * @param {(Function|Object)} stateful - The (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state.
 * @param {string} id - The ID of the participant's organization to retrieve.
 * @returns {string}
 */
export function getParticipantOrganization(
        stateful: Object | Function,
        id: string) {
    const participant = getParticipantById(stateful, id);

    return participant?.organization;
}

/**
 * Curried version of getParticipantDisplayName.
 *
 * @see {@link getParticipantDisplayName}
 * @param {string} id - The ID of the participant's display name to retrieve.
 * @returns {Function}
 */
export const getParticipantDisplayNameWithId = (id: string) =>
    (state: Object | Function) =>
        getParticipantDisplayName(state, id);

/**
 * Returns the presence status of a participant associated with the passed id.
 *
 * @param {(Function|Object)} stateful - The (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state.
 * @param {string} id - The id of the participant.
 * @returns {string} - The presence status.
 */
export function getParticipantPresenceStatus(
        stateful: Object | Function, id: string) {
    if (!id) {
        return undefined;
    }
    const participantById = getParticipantById(stateful, id);

    if (!participantById) {
        return undefined;
    }

    return participantById.presence;
}

/**
 * Selectors for getting all known participants with fake participants filtered
 * out.
 *
 * @param {(Function|Object|Participant[])} stateful - The redux state
 * features/base/participants, the (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state
 * features/base/participants.
 * @returns {Participant[]}
 */
export function getParticipants(stateful: Object | Function) {
    return _getAllParticipants(stateful).filter(p => !p.isFakeParticipant);
}

/**
 * Returns the participant which has its pinned state set to truthy.
 *
 * @param {(Function|Object|Participant[])} stateful - The redux state
 * features/base/participants, the (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state
 * features/base/participants.
 * @returns {(Participant|undefined)}
 */
export function getPinnedParticipant(stateful: Object | Function) {
    return _getAllParticipants(stateful).find(p => p.pinned);
}

/**
 * Returns screenshare participant's display name.
 *
 * @param {(Function|Object)} stateful - The (whole) redux state, or redux's {@code getState} function to be used to
 * retrieve the state.
 * @param {string} id - The ID of the screenshare participant's display name to retrieve.
 * @returns {string}
 */
export function getScreenshareParticipantDisplayName(stateful, id) {
    const ownerDisplayName = getParticipantDisplayName(stateful, getVirtualScreenshareParticipantOwnerId(id));

    return i18next.t('screenshareDisplayName', { name: ownerDisplayName });
}

/**
 * Returns local screen share participant from Redux state.
 *
 * @param {(Function|Object)} stateful - The (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state features/base/participants.
 * @returns {(IParticipant|undefined)}
 */
export function getLocalScreenShareParticipant(stateful) {
    const state = toState(stateful)['features/base/participants'];

    return state.localScreenShare;
}

/**
 * Returns array of participants from Redux state.
 *
 * @param {(Function|Object|Participant[])} stateful - The redux state
 * features/base/participants, the (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state
 * features/base/participants.
 * @private
 * @returns {Participant[]}
 */
function _getAllParticipants(stateful) {
    return (
        Array.isArray(stateful)
            ? stateful
            : toState(stateful)['features/base/participants'] || []);
}

/**
 * Returns the youtube fake participant.
 * At the moment it is considered the youtube participant the only fake participant in the list.
 *
 * @param {(Function|Object|Participant[])} stateful - The redux state
 * features/base/participants, the (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state
 * features/base/participants.
 * @private
 * @returns {Participant}
 */
export function getYoutubeParticipant(stateful: Object | Function) {
    const participants = _getAllParticipants(stateful);

    return participants.filter(p => p.isFakeParticipant)[0];
}

/**
 * Returns true if the participant is a moderator.
 *
 * @param {string} participant - Participant object.
 * @returns {boolean}
 */
export function isParticipantModerator(participant: Object) {
    return participant?.role === PARTICIPANT_ROLE.MODERATOR;
}

/**
 * Returns true if all of the meeting participants are moderators.
 *
 * @param {Object|Function} stateful -Object or function that can be resolved
 * to the Redux state.
 * @returns {boolean}
 */
export function isEveryoneModerator(stateful: Object | Function) {
    const participants = _getAllParticipants(stateful);

    return participants.every(isParticipantModerator);
}

/**
 * Checks a value and returns true if it's a preloaded icon object.
 *
 * @param {?string | ?Object} icon - The icon to check.
 * @returns {boolean}
 */
export function isIconUrl(icon: ?string | ?Object) {
    return Boolean(icon) && (typeof icon === 'object' || typeof icon === 'function');
}

/**
 * Returns true if the current local participant is a moderator in the
 * conference.
 *
 * @param {Object|Function} stateful - Object or function that can be resolved
 * to the Redux state.
 * @returns {boolean}
 */
export function isLocalParticipantModerator(stateful: Object | Function) {
    const state = toState(stateful);
    const localParticipant = getLocalParticipant(state);

    if (!localParticipant) {
        return false;
    }

    return isParticipantModerator(localParticipant);
}

/**
 * Returns true if the local participant is force muted for audio.
 *
 * @param {Object|Function} stateful - Object or function that can be resolved
 * to the Redux state.
 * @returns {boolean}
 */
export function isLocalParticipantForceMutedAudio(stateful: Object | Function) {
    const state = toState(stateful);

    if (isLocalParticipantModerator(state)) {
        return false;
    }

    const localParticipant = getLocalParticipant(state);
    let localId = localParticipant?.id;

    if (!localId && typeof APP !== 'undefined' && APP?.conference?.getMyUserId) {
        localId = APP.conference.getMyUserId();
    }

    if (!localId) {
        return false;
    }

    const lockAudio = state['features/base/conference']?.lockAudio;

    return Boolean(lockAudio?.[localId]);
}

/**
 * Returns true if the local participant is force muted for video.
 *
 * @param {Object|Function} stateful - Object or function that can be resolved
 * to the Redux state.
 * @returns {boolean}
 */
export function isLocalParticipantForceMutedVideo(stateful: Object | Function) {
    const state = toState(stateful);

    if (isLocalParticipantModerator(state)) {
        return false;
    }

    const localParticipant = getLocalParticipant(state);
    let localId = localParticipant?.id;

    if (!localId && typeof APP !== 'undefined' && APP?.conference?.getMyUserId) {
        localId = APP.conference.getMyUserId();
    }

    if (!localId) {
        return false;
    }

    const lockVideo = state['features/base/conference']?.lockVideo;

    return Boolean(lockVideo?.[localId]);
}

/**
 * Returns true if the video of the participant should be rendered.
 * NOTE: This is currently only used on mobile.
 *
 * @param {Object|Function} stateful - Object or function that can be resolved
 * to the Redux state.
 * @param {string} id - The ID of the participant.
 * @returns {boolean}
 */
export function shouldRenderParticipantVideo(stateful: Object | Function, id: string) {
    const state = toState(stateful);
    const participant = getParticipantById(state, id);

    if (!participant) {
        return false;
    }

    /* First check if we have an unmuted video track. */
    const { getVideoTrackByParticipant } = require('../tracks/functions');
    const videoTrack = getVideoTrackByParticipant(state, participant);

    if (!videoTrack) {
        return false;
    }

    if (!shouldRenderVideoTrack(videoTrack, /* waitForVideoStarted */ false)) {
        return false;
    }

    /* Then check if the participant connection is active. */
    const connectionStatus = participant.connectionStatus || MHParticipantConnectionStatus.ACTIVE;

    if (connectionStatus !== MHParticipantConnectionStatus.ACTIVE) {
        return false;
    }

    /* Then check if audio-only mode is not active. */
    const audioOnly = state['features/base/audio-only'].enabled;

    if (!audioOnly) {
        return true;
    }

    /* Last, check if the participant is sharing their screen and they are on stage. */
    const remoteScreenShares = state['features/video-layout'].remoteScreenShares || [];
    const largeVideoParticipantId = state['features/large-video'].participantId;
    const participantIsInLargeVideoWithScreen
        = participant.id === largeVideoParticipantId && remoteScreenShares.includes(participant.id);

    return participantIsInLargeVideoWithScreen;
}

/**
 * Resolves the first loadable avatar URL for a participant.
 *
 * @param {Object} participant - The participant to resolve avatars for.
 * @param {Store} store - Redux store.
 * @returns {?string}
 */
async function _getFirstLoadableAvatarUrl(participant, store) {
    for (let i = 0; i < AVATAR_CHECKER_FUNCTIONS.length; i++) {
        const url = AVATAR_CHECKER_FUNCTIONS[i](participant, store);

        if (url) {
            if (AVATAR_CHECKED_URLS.has(url)) {
                if (AVATAR_CHECKED_URLS.get(url)) {
                    return url;
                }
            } else {
                try {
                    const finalUrl = await preloadImage(url).catch(e => logger.debug('Error', e));

                    AVATAR_CHECKED_URLS.set(finalUrl, true);

                    return finalUrl;
                } catch (e) {
                    AVATAR_CHECKED_URLS.set(url, false);
                }
            }
        }
    }

    return undefined;
}

/**
 * Get the participants queue with raised hands.
 *
 * @param {(Function|Object)} stateful - The (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state
 * features/base/participants.
 * @returns {Array<Object>}
 */
export function getRaiseHandsQueue(stateful: Object | Function) {

    const { raisedHandsQueue } = toState(stateful)['features/base/participants'];

    return raisedHandsQueue;
}

/**
 * Returns whether the given participant has his hand raised or not.
 *
 * @param {Object} participant - The participant.
 * @returns {boolean} - Whether participant has raise hand or not.
 */
export function hasRaisedHand(participant: Object) {
    return Boolean(participant?.raisedHand);
}

/**
 * Returns whether the fake participant is a remote screenshare.
 *
 * @param {IParticipant|undefined} participant - The participant entity.
 * @returns {boolean} - True if it's a remote screenshare participant.
 */
export function isRemoteScreenshareParticipant(participant?: Object): boolean {
    return participant?.fakeParticipant === 'RemoteScreenShare';
}

/**
 * Selectors for getting all remote participants.
 *
 * @param {(Function|Object)} stateful - The (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state
 * features/base/participants.
 * @returns {Map<string, Object>}
 */
export function getRemoteParticipants(stateful: Object | Function) {
    const participants = toState(stateful)['features/base/participants'] || [];

    return participants.filter(p => !p?.local && !p?.fakeParticipant);
}

/**
 * Returns whether the fake participant is a local screenshare.
 *
 * @param {IParticipant|undefined} participant - The participant entity.
 * @returns {boolean} - True if it's a local screenshare participant.
 */
export function isLocalScreenshareParticipant(participant?: Object): boolean {
    return participant?.fakeParticipant === 'LocalScreenShare';
}

/**
 * Returns whether the fake participant is a whiteboard.
 *
 * @param {IParticipant|undefined} participant - The participant entity.
 * @returns {boolean} - True if it's a whiteboard participant.
 */
export function isWhiteboardParticipant(participant?: Object): boolean {
    return participant?.fakeParticipant === 'Whiteboard';
}

/**
 * The tracks state {@link getScreenshareParticipantIds} last computed from, and its result.
 */
let _screenshareIdsTracks;
let _screenshareIds = [];

/**
 * Returns a list of IDs of the participants that are currently screensharing.
 *
 * @param {(Function|Object)} stateful - The (whole) redux state, or redux's {@code getState} function to be used to
 * retrieve the state.
 * @returns {Array<string>}
 */
export function getScreenshareParticipantIds(stateful: Object): Array<string> {
    const tracks = toState(stateful)['features/base/tracks'];

    // Reuse the previous result while the tracks state is the same array. A new array on every call failed
    // react-redux's shallow comparison, so the toolbox re-rendered on every action. Callers only read the result.
    if (tracks !== _screenshareIdsTracks) {
        _screenshareIdsTracks = tracks;
        _screenshareIds = tracks
            .filter(track => track.videoType === VIDEO_TYPE.DESKTOP && !track.muted)
            .map(t => t.participantId);
    }

    return _screenshareIds;
}

/**
 * Returns whether the fake participant is of local or virtual screenshare type.
 *
 * @param {Object|undefined} participant - The participant entity.
 * @returns {boolean} - True if it's one of the two.
 */
export function isScreenShareParticipant(participant?: Object): boolean {
    return isLocalScreenshareParticipant(participant) || isRemoteScreenshareParticipant(participant);
}

/**
 * return the participant id who shared the screen.
 *
 * @param {Object|undefined} participant - The participant entity.
 * @returns {boolean} - True if it's one of the two.
 */
export function getScreenSharingParticipantId(id) {
    return getVirtualScreenshareParticipantOwnerId(id);
}

/**
 * Returns whether the (fake) participant is a shared video.
 *
 * @param {IParticipant|undefined} participant - The participant entity.
 * @returns {boolean} - True if it's a shared video participant.
 */
export function isSharedVideoParticipant(participant): boolean {
    return participant?.fakeParticipant === FakeParticipant.SharedVideo;
}

/**
 * Returns whether the fake participant is of local or virtual screenshare type.
 *
 * @param {Object} state - The (whole) redux state, or redux's.
 * @param {string|undefined} participantId - The participant id.
 * @returns {boolean} - True if it's one of the two.
 */
export function isScreenShareParticipantById(state: Object, participantId?: string): boolean {
    const participant = getParticipantByIdOrUndefined(state, participantId);

    return isScreenShareParticipant(participant);
}

/**
 * Returns the dominant speaker participant.
 *
 * @param {(Function|Object)} stateful - The (whole) redux state or redux's
 * {@code getState} function to be used to retrieve the state features/base/participants.
 * @returns {IParticipant} - The participant from the redux store.
 */
export function getDominantSpeakerParticipant(stateful) {
    const state = toState(stateful)['features/base/participants'];

    const dominantParticipant = state.filter(participant => participant.dominantSpeaker);

    return dominantParticipant;
}

/**
 * Returns screenshare participant.
 *
 * @param {(Function|Object)} stateful - The (whole) redux state, or redux's {@code getState} function to be used to
 * retrieve the state features/base/participants.
 * @param {string} id - The owner ID of the screenshare participant to retrieve.
 * @returns {(IParticipant|undefined)}
 */
export function getVirtualScreenshareParticipantByOwnerId(stateful: IStateful, id: string) {
    const state = toState(stateful);
    const track = getScreenShareTrack(state['features/base/tracks'], id);

    return getParticipantById(stateful, track?.mhTrack.getSourceName());
}

/**
 * Add people feature enabling/disabling.
 *
 * @param {Object|Function} stateful - Object or function that can be resolved
 * to the Redux state.
 * @returns {boolean}
 */
export const addPeopleFeatureControl = (stateful: IStateful) => {
    const state = toState(stateful);

    return getFeatureFlag(state, ADD_PEOPLE_ENABLED, true)
    && (require('../../invite/functions').isAddPeopleEnabled(state)
        || require('../../invite/functions').isDialOutEnabled(state));
};

export const isSharedThumbnailEnabled = (stateful: IStateful): string | null => {
    const state = toState(stateful);
    const sharingWhiteBoard = state['features/genericiframe'].visible;
    const {
        editing: shareEtherpad
    } = state['features/etherpad'];

    return sharingWhiteBoard ? 'genericIframe' : shareEtherpad ? 'etherpad' : null;
};

/**
 * Checks if the local participant is currently sharing the whiteboard.
 *
 * @param {Object|Function} stateful - Object or function that can be resolved
 * to the Redux state.
 * @returns {boolean}
 */
export function isLocalParticipantSharingWhiteboard(stateful) {
    const state = toState(stateful);
    const localParticipant = getLocalParticipant(state);
    const whiteboardPartId = state['features/genericiframe']?.partId;

    return Boolean(localParticipant && whiteboardPartId === localParticipant.id);
}

/**
 * Checks if the local participant is currently sharing the livepad (etherpad).
 *
 * @param {Object|Function} stateful - Object or function that can be resolved
 * to the Redux state.
 * @returns {boolean}
 */
export function isLocalParticipantSharingLivepad(stateful) {
    const state = toState(stateful);
    const localParticipant = getLocalParticipant(state);
    const etherpadPartId = state['features/etherpad']?.partId;

    return Boolean(localParticipant && etherpadPartId === localParticipant.id);
}

/**
 * Checks if the local participant is sharing whiteboard or livepad.
 *
 * @param {Object|Function} stateful - Object or function that can be resolved
 * to the Redux state.
 * @returns {{ isSharingWhiteboard: boolean, isSharingLivepad: boolean }}
 */
export function isLocalParticipantSharingWhiteboardOrLivepad(stateful) {
    return {
        isSharingWhiteboard: isLocalParticipantSharingWhiteboard(stateful),
        isSharingLivepad: isLocalParticipantSharingLivepad(stateful)
    };
}

/**
 * Returns all participant IDs.
 *
 * @param {(Function|Object)} stateful - The (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state
 * features/base/participants.
 * @returns {Array<string>}
 */
export function getAllParticipantAndSharedFrames(stateful) {
    const state = toState(stateful);
    const participants = state['features/base/participants'];
    const participantIds = participants.map(participant => participant.id);
    const sharedThumbnailEnabled = isSharedThumbnailEnabled(state);
    const sharedThumbnailList = sharedThumbnailEnabled ? [ sharedThumbnailEnabled ] : [];

    return [ ...participantIds, ...sharedThumbnailList ];
}

/**
 * Returns all fake participants Length.
 * @param {Object|Function} stateful - Object or function that can be resolved
 * to the Redux state.
 */
export function getAllFakeParticipantsLength(stateful) {
    const state = toState(stateful);
    const participants = state['features/base/participants'];
    const screenSharingParticipants = participants.filter(participant => participant.isFakeParticipant === 'RemoteScreenShare' || participant.isFakeParticipant === 'LocalScreenShare');
    const sharedThumbnailEnabled = isSharedThumbnailEnabled(state);
    const youtubeParticipant = participants.filter(participant => participant.isFakeParticipant && participant.name === 'YouTube');

    return screenSharingParticipants.length + youtubeParticipant.length + (sharedThumbnailEnabled ? 1 : 0);
}

/**
 * Returns all fake participants.
 *
 * @param {(Function|Object)} stateful - The (whole) redux state, or redux's
 * {@code getState} function to be used to retrieve the state
 * features/base/participants.
 * @returns {Array<Object>}
 */
export function getFakeParticipants(stateful: Object | Function) {
    const participants = _getAllParticipants(stateful);

    return participants.filter(p => p.isFakeParticipant);
}

/**
 * Returns the shared video fake participant.
 *
 * @param {Object|Function} stateful - The redux state or getState function.
 * @returns {(Object|undefined)}
 */
export function getSharedVideoParticipant(stateful: IStateful): IParticipant | undefined {

    const participants = _getAllParticipants(stateful);

    return participants.find(p => p.fakeParticipant === FakeParticipant.SharedVideo);
}

let lastModeratorPinTimes = [];

/**
 * Checks and updates rate limit for moderator pin (Pin for everyone).
 *
 * @param {Function} dispatch - Redux dispatch function.
 * @param {Function} [t] - Translation function.
 * @returns {boolean} - True if rate limited, false otherwise.
 */
export function checkModeratorPinRateLimit(dispatch: Function, t?: Function): boolean {
    const now = Date.now();

    lastModeratorPinTimes = (lastModeratorPinTimes || []).filter(time => now - time < 10000);
    if (lastModeratorPinTimes.length >= 5) {
        if (typeof dispatch === 'function') {
            const titleKey = t ? t('chat.moderatorPinRateLimitKey') : 'chat.moderatorPinRateLimitKey';
            const descriptionKey = t ? t('chat.moderatorPinRateLimitMessage') : 'chat.moderatorPinRateLimitMessage';

            dispatch(showWarningNotification({
                titleKey,
                description: descriptionKey
            }));
        }

        return true;
    }
    lastModeratorPinTimes.push(now);

    return false;
}
